import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import SplitPaymentFields from '../common/SplitPaymentFields';
import CustomerDropdown from '../customers/CustomerDropdown';
import { FIELD_PLACEHOLDER_COLOR, fieldLabelSx } from '../common/formFieldStyles';
import { formatCurrency } from '../../utils/currencyUtils';
import {
  createDefaultSplitPaymentLines,
  toCheckoutPayload,
  toSplitAmount,
  validateSplitPaymentLines,
} from '../../utils/splitPaymentUtils';
import ProductSaleLinesField, { getProductLinesTotal } from './ProductSaleLinesField';
import { BRAND } from '../../theme/brandTokens';

const EMPTY_SELLER = '';

const buildTextFieldSx = () => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  minHeight: 96,
  resize: 'vertical',
  '&:focus': {
    borderColor: BRAND.accent,
  },
});

const RecordProductSaleDialog = ({
  open,
  onClose,
  onSave,
  saving = false,
  storeId,
  products = [],
  teamMembers = [],
  initialProductId = null,
  currencyCode,
  commissionPercent = 0,
  currentTeamMemberId = null,
  canAssignSeller = false,
}) => {
  const { t } = useTranslation();
  const lockedSellerId = currentTeamMemberId || EMPTY_SELLER;
  const [lines, setLines] = useState([]);
  const [teamMemberId, setTeamMemberId] = useState(canAssignSeller ? EMPTY_SELLER : lockedSellerId);
  const [customer, setCustomer] = useState(null);
  const [notes, setNotes] = useState('');
  const [paymentLines, setPaymentLines] = useState(() => createDefaultSplitPaymentLines(0));
  const [errors, setErrors] = useState({});

  const cartTotal = useMemo(() => toSplitAmount(getProductLinesTotal(products, lines)), [products, lines]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const initialSeller = canAssignSeller ? EMPTY_SELLER : lockedSellerId;
    setLines(initialProductId ? [{ productId: initialProductId, quantity: 1 }] : []);
    setTeamMemberId(initialSeller);
    setCustomer(null);
    setNotes('');
    setPaymentLines(createDefaultSplitPaymentLines(0));
    setErrors({});
  }, [open, canAssignSeller, lockedSellerId, initialProductId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPaymentLines(createDefaultSplitPaymentLines(cartTotal));
  }, [open, cartTotal]);

  const commissionAmount = teamMemberId
    ? Math.round(((cartTotal * (Number(commissionPercent) || 0)) / 100) * 100) / 100
    : 0;

  const handleSubmit = () => {
    const nextErrors = {};
    if (!lines.length) {
      nextErrors.lines = 'productsManagement.sale.validation.noLines';
    }

    const paymentResult = validateSplitPaymentLines({
      lines: paymentLines,
      amountDue: cartTotal,
      allowDiscount: true,
    });

    setErrors({ ...nextErrors, ...paymentResult.errors });
    if (Object.keys(nextErrors).length > 0 || !paymentResult.isValid) {
      return;
    }

    const checkout = toCheckoutPayload({
      payments: paymentResult.payments,
      collected: paymentResult.collected,
      amountDue: cartTotal,
    });

    onSave({
      lines,
      teamMemberId: teamMemberId || null,
      customerId: customer?.customerId || null,
      notes: notes.trim() || null,
      paymentMethod: checkout.paymentMethod,
      amountReceived: checkout.amountReceived,
      payments: checkout.payments,
      discount: checkout.discount,
    });
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      title={t('productsManagement.sale.title')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} disabled={saving} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.saving') : t('productsManagement.sale.save')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <ProductSaleLinesField
          products={products.filter((product) => !product.disabled)}
          lines={lines}
          onChange={setLines}
          currencyCode={currencyCode}
        />
        {errors.lines ? (
          <Typography variant="caption" sx={{ color: '#f44336' }}>
            {t(errors.lines)}
          </Typography>
        ) : null}

        <SalovareSelectField
          label={t('productsManagement.sale.professional')}
          value={canAssignSeller ? teamMemberId : lockedSellerId}
          onChange={(event) => setTeamMemberId(event.target.value)}
          disabled={!canAssignSeller}
          displayEmpty
          renderValue={(value) => {
            if (!value) {
              return (
                <Typography component="span" sx={{ color: FIELD_PLACEHOLDER_COLOR }}>
                  {t('productsManagement.sale.noProfessional')}
                </Typography>
              );
            }
            const member = teamMembers.find((item) => item.id === value);
            return member?.name || value;
          }}
        >
          <MenuItem value={EMPTY_SELLER}>
            <Typography sx={{ color: FIELD_PLACEHOLDER_COLOR }}>
              {t('productsManagement.sale.noProfessional')}
            </Typography>
          </MenuItem>
          {teamMembers.map((member) => (
            <MenuItem key={member.id} value={member.id}>
              {member.name}
            </MenuItem>
          ))}
        </SalovareSelectField>
        {!canAssignSeller ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: -2 }}>
            {t('productsManagement.sale.professionalLocked')}
          </Typography>
        ) : null}

        <CustomerDropdown
          storeId={storeId}
          enabled={open}
          label={t('productsManagement.sale.customer')}
          placeholder={t('productsManagement.sale.walkIn')}
          value={customer}
          onChange={setCustomer}
          disabled={saving}
        />

        {(canAssignSeller ? teamMemberId : lockedSellerId) ? (
          <Typography variant="body2" sx={{ color: BRAND.accentText, fontWeight: 600 }}>
            {t('productsManagement.sale.commissionHint', {
              amount: formatCurrency(commissionAmount, currencyCode),
            })}
          </Typography>
        ) : null}

        <SplitPaymentFields
          lines={paymentLines}
          onChange={setPaymentLines}
          amountDue={cartTotal}
          currencyCode={currencyCode}
          allowDiscount
          disabled={saving}
          errors={errors}
        />

        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>
            {t('productsManagement.sale.notes')}
          </Typography>
          <Box
            component="textarea"
            aria-label={t('productsManagement.sale.notes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            sx={buildTextFieldSx()}
          />
        </Box>
      </Box>
    </ScrollableDialog>
  );
};

export default RecordProductSaleDialog;
